<template>
  <div class="mention-wrapper">
    <!-- Slot for custom textarea -->
    <ul v-if="showDropdown" class="dropdown">
      <li
        v-for="(user, index) in users"
        :key="user.id"
        @mousedown.prevent="selectUser(user)"
        :class="{ highlighted: index == highlightedIndex }"
      >
        {{ user.name }}
      </li>
    </ul>
    <input
      :disabled="disabled"
      type="text"
      placeholder="Text"
      v-model="text"
      :style="inputStyles"
      @keydown="onKeyDown"
    />
  </div>
</template>

<script lang="ts">
import { UserData } from "@/store/types"
import { UserType } from "@/types/user"
import { defineComponent, PropType } from "vue"

export default defineComponent({
  name: "MentionComponent",
  props: {
    asyncSearch: {
      type: Function as PropType<(query: string) => Promise<UserData[]>>,
      required: true,
    },
    modelValue: {
      type: String,
      default: "",
    },
    inputStyles: {
      type: [String, Object],
      default: "",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      text: this.modelValue,
      users: [] as UserData[],
      showDropdown: false,
      query: "",
      highlightedIndex: 0,
      searchTimeout: undefined as undefined | number,
    }
  },
  watch: {
    modelValue(newVal) {
      this.text = newVal
    },
    users() {
      this.highlightedIndex = 0
    },
    text() {
      this.$emit("update:modelValue", this.text)
      const match = this.text.match(/@(\w*)$/)
      if (match) {
        this.query = match[1]

        // Debounce async search
        clearTimeout(this.searchTimeout)
        this.searchTimeout = setTimeout(async () => {
          this.users = await this.asyncSearch(this.query)
          this.showDropdown = this.users.length > 0
          this.highlightedIndex = 0
        }, 300)
      } else {
        this.showDropdown = false
      }
    },
  },
  methods: {
    selectUser(user: UserType) {
      this.text = this.text.replace(/@\w*$/, `@${user.name} `)
      this.query = user.name
      this.showDropdown = false
      this.$emit("update:modelValue", this.text)
    },
    highlightNext() {
      if (this.highlightedIndex < this.users.length - 1) {
        this.highlightedIndex++
      }
    },
    highlightPrev() {
      if (this.highlightedIndex > 0) {
        this.highlightedIndex--
      }
    },
    selectHighlighted() {
      if (this.users[this.highlightedIndex]) {
        this.selectUser(this.users[this.highlightedIndex])
      }
    },
    updateText(newText: string) {
      this.text = newText
      this.$emit("update:modelValue", this.text)
    },
    onKeyDown(e: KeyboardEvent) {
      if (!this.users) return

      if (e.key === "ArrowDown") {
        e.preventDefault()
        this.highlightNext()
      } else if (e.key === "ArrowUp") {
        e.preventDefault()
        this.highlightPrev()
      } else if (e.key === "Enter" && this.showDropdown) {
        e.preventDefault()
        const user = this.users[this.highlightedIndex]
        if (user) this.selectUser(user)
      } else if (e.key === "Escape") {
        this.showDropdown = false
      }
    },
  },
})
</script>

<style lang="scss" scoped>
.mention-wrapper {
  position: relative;
  width: 300px;
  .dropdown {
    position: absolute;
    bottom: 35px;
    left: 0;
    width: 100%;
    list-style: none;
    padding: 0;
    margin: 0;
    max-height: 150px;
    overflow-y: auto;
    z-index: 10;
    border: 1px solid rgba(255, 255, 255, 0.12);

    li {
      padding: 8px;
      cursor: pointer;
      background-color: #2d2f33;
      color: white;
      &:hover,
      &.highlighted {
        background: var(--text-gradient);
        color: white;
      }
    }
  }
}
</style>
